/* eslint-disable @typescript-eslint/no-explicit-any */
import { useDispatch } from "react-redux";
import React, { useState, useEffect, JSX } from "react";
import { Box, Chip, Divider, TextField, Typography } from "@mui/material";

import QrImageUploader from "./QrImageUploader";
import { tl } from "../../../../components/translate";
import { updateQrData } from "../../../../actions/resourceCentre";
import { TQrPaymentInfo } from "../../../../interfaces/QrPayment";
import { StyledTab, StyledTabs } from "../../../../components/StyledTabs";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import { QrPaymentMethodsEnum } from "../../../../enum/billing";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";

export const qrPaymentMethods = [
  { value: "esewa", label: "eSewa" },
  { value: "khalti", label: "Khalti" },
  { value: "fonepay", label: "Fonepay" },
  { value: "bank", label: "Bank" },
  { value: "nepalPay", label: "Nepal Pay" }
];

interface QrPaymentInfoFormProps {
  resourceCentre: ResourceCentre;
}

const QrPaymentInfoForm = ({ resourceCentre }: QrPaymentInfoFormProps): JSX.Element => {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();

  const [activeTab, setActiveTab] = useState<QrPaymentMethodsEnum>(
    qrPaymentMethods[0].value as QrPaymentMethodsEnum
  );
  const [qrImage, setQrImage] = useState<string>("");
  const [qrPaymentInfo, setQrPaymentInfo] = useState<TQrPaymentInfo>(() => {
    const qrPymentInfo = rc?.qrPaymentInfo || {};
    return qrPymentInfo;
  });

  useEffect(() => {
    if (
      [
        QrPaymentMethodsEnum.ESEWA,
        QrPaymentMethodsEnum.KHALTI,
        QrPaymentMethodsEnum.FONEPAY,
        QrPaymentMethodsEnum.BANK
      ].includes(activeTab as QrPaymentMethodsEnum)
    ) {
      const key = activeTab as keyof TQrPaymentInfo;
      const url = qrPaymentInfo?.[key]?.images?.[0]?.url || "";
      setQrImage(url);
    }
  }, [activeTab, qrPaymentInfo]);

  const handleUpdateQrPaymentInfo = async () => {
    await dispatch(updateQrData(Number(resourceCentre?.id), qrPaymentInfo));
  };

  const handlePaymentInfoChange = ({
    paymentMethod,
    infoType,
    infoField,
    value
  }: {
    paymentMethod: keyof TQrPaymentInfo;
    infoType: string;
    infoField: string;
    value: string;
  }) => {
    setQrPaymentInfo((prev) => {
      const methodInfo = prev[paymentMethod] || {};
      const typeArray = (methodInfo as any)[infoType] || [{}];

      const updatedMethodInfo = {
        ...methodInfo,
        [infoType]: [
          {
            ...typeArray[0],
            [infoField]: value
          }
        ]
      };

      return {
        ...prev,
        [paymentMethod]: updatedMethodInfo
      };
    });
  };

  return (
    <Box>
      <Typography style={{ fontWeight: 600 }}>{tl("settings.qrPaymentReceiptMethod")}</Typography>
      <StyledTabs value={activeTab} onChange={(e, tab) => setActiveTab(tab)}>
        {qrPaymentMethods
          .filter((p) => p.value !== QrPaymentMethodsEnum.NEPALPAY)
          .map((method) => (
            <StyledTab
              key={method.value}
              value={method.value}
              label={tl(`settings.qrPaymentReceiptMethod.${method.value}`)}
            />
          ))}
      </StyledTabs>

      <Box marginTop="20px" sx={{ width: "100%", marginTop: "20px" }}>
        {[
          QrPaymentMethodsEnum.ESEWA,
          QrPaymentMethodsEnum.KHALTI,
          QrPaymentMethodsEnum.FONEPAY,
          QrPaymentMethodsEnum.BANK
        ].includes(activeTab as QrPaymentMethodsEnum) && (
          <QrImageUploader
            initialImage={qrImage}
            activePaymentMethod={activeTab as keyof TQrPaymentInfo}
            rcId={resourceCentre?.id}
            qrPaymentInfo={qrPaymentInfo}
            setQrPaymentInfo={setQrPaymentInfo}
          />
        )}

        {[QrPaymentMethodsEnum.ESEWA, QrPaymentMethodsEnum.KHALTI].includes(
          activeTab as QrPaymentMethodsEnum
        ) && (
          <Divider style={{ marginTop: "30px", marginBottom: "30px" }}>
            <Chip label="Or Provide Credentials" size="small" />
          </Divider>
        )}

        {/* esewa */}
        {activeTab === QrPaymentMethodsEnum.ESEWA && (
          <Box display="flex" flexDirection="column" gap="20px">
            <TextField
              label="Esewa ID"
              placeholder="Enter Esewa ID/Number"
              value={qrPaymentInfo?.esewa?.credentials?.[0]?.id || ""}
              variant="outlined"
              type="text"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              onChange={(e) =>
                handlePaymentInfoChange({
                  paymentMethod: "esewa",
                  infoType: "credentials",
                  infoField: "id",
                  value: e.target.value
                })
              }
              onBlur={() => handleUpdateQrPaymentInfo()}
            />

            <TextField
              label="Name"
              placeholder="Enter Esewa Name"
              value={qrPaymentInfo?.esewa?.credentials?.[0]?.name || ""}
              variant="outlined"
              type="text"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              onChange={(e) =>
                handlePaymentInfoChange({
                  paymentMethod: "esewa",
                  infoType: "credentials",
                  infoField: "name",
                  value: e.target.value
                })
              }
              onBlur={() => handleUpdateQrPaymentInfo()}
            />
          </Box>
        )}

        {/* khalti */}
        {activeTab === QrPaymentMethodsEnum.KHALTI && (
          <Box display="flex" flexDirection="column" gap="20px">
            <TextField
              label="Khalti ID"
              placeholder="Enter Khalti ID/Number"
              value={qrPaymentInfo?.khalti?.credentials?.[0]?.id || ""}
              variant="outlined"
              type="text"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              onChange={(e) =>
                handlePaymentInfoChange({
                  paymentMethod: "khalti",
                  infoType: "credentials",
                  infoField: "id",
                  value: e.target.value
                })
              }
              onBlur={() => handleUpdateQrPaymentInfo()}
            />
            <TextField
              label="Name"
              placeholder="Enter Khalti Name"
              value={qrPaymentInfo?.khalti?.credentials?.[0]?.name || ""}
              variant="outlined"
              type="text"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              onChange={(e) =>
                handlePaymentInfoChange({
                  paymentMethod: "khalti",
                  infoType: "credentials",
                  infoField: "name",
                  value: e.target.value
                })
              }
              onBlur={() => handleUpdateQrPaymentInfo()}
            />
          </Box>
        )}

        {/**
         * Fonepay credentials is not implemented yet because
         * currently we dont have enough information on what params we need to generate its QR code
         */}
      </Box>
    </Box>
  );
};

export default QrPaymentInfoForm;
